import { apiRowToSdeRow, apiRowsToSdeRows, shouldKeepSdeRow } from "@/utils/extraction-api-bridge";
import { hydrateRowsMetadataFields, reindexSdeRows } from "@/utils/sde-row-utils";

/**
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

/**
 * @param {SdeRow[]} existingRows
 * @returns {Record<string, SdeRow>}
 */
function indexByExtractionSeq(existingRows) {
    /** @type {Record<string, SdeRow>} */
    const index = {};
    existingRows.forEach((row) => {
        if (row.extractionSeq != null) {
            index[String(row.extractionSeq)] = row;
        }
    });
    return index;
}

/**
 * @param {SdeRow[]} existingRows
 * @returns {Record<string, SdeRow>}
 */
function indexByUpc(existingRows) {
    /** @type {Record<string, SdeRow>} */
    const index = {};
    existingRows.forEach((row) => {
        const upc = row.fields?.upc?.value;
        if (upc != null && upc !== "") {
            index[String(upc)] = row;
        }
    });
    return index;
}

/**
 * @param {SdeRow} targetRow
 * @param {SdeRow['fields']} incomingFields
 */
function mergeRowFields(targetRow, incomingFields) {
    Object.entries(incomingFields || {}).forEach(([key, incoming]) => {
        const local = targetRow.fields[key];
        if (local?.confidence?.level === "manual") {
            return;
        }
        if (
            local &&
            local.value !== incoming.value &&
            local.confidence?.aiValue != null
        ) {
            return;
        }
        targetRow.fields[key] = {
            ...incoming,
            key,
        };
    });
}

/**
 * @param {SdeRow[]} rows
 * @returns {number}
 */
export function maxExtractionSeq(rows) {
    return (rows || []).reduce((max, row) => {
        const seq = row.extractionSeq;
        if (typeof seq === "number" && seq > max) {
            return seq;
        }
        return max;
    }, 0);
}

/**
 * @param {SdeRow[]} existingRows
 * @param {SdeRow[]} incomingRows
 * @returns {SdeRow[]}
 */
export function mergeIncomingRows(existingRows, incomingRows) {
    const bySeq = indexByExtractionSeq(existingRows);
    const byUpc = indexByUpc(existingRows);
    const result = [...existingRows];
    incomingRows.forEach((incoming) => {
        const seq = incoming.extractionSeq;
        if (seq != null && bySeq[String(seq)]) {
            mergeRowFields(bySeq[String(seq)], incoming.fields);
            return;
        }
        const upc = incoming.fields?.upc?.value;
        const match = upc != null && upc !== "" ? byUpc[String(upc)] : null;
        if (match) {
            mergeRowFields(match, incoming.fields);
            return;
        }
        result.push(incoming);
        if (seq != null) {
            bySeq[String(seq)] = incoming;
        }
        if (upc != null && upc !== "") {
            byUpc[String(upc)] = incoming;
        }
    });
    return reindexSdeRows(result);
}

/**
 * @param {{ rows?: SdeRow[], deletedMetadataKeys?: string[], metadataColumnKeys?: string[] }} draft
 * @returns {SdeRow[]}
 */
export function restoreRowsFromDraft(draft) {
    if (!draft?.rows?.length) {
        return [];
    }
    const rows = applyDeletedMetadataKeys(reindexSdeRows(draft.rows), draft.deletedMetadataKeys);
    const filtered = rows.filter(shouldKeepSdeRow);
    return hydrateRowsMetadataFields(filtered, draft.metadataColumnKeys);
}

/**
 * @param {SdeRow[]} serverRows
 * @param {{ rows?: SdeRow[], deletedMetadataKeys?: string[], metadataColumnKeys?: string[] }} draft
 * @returns {SdeRow[]}
 */
export function mergeDraftOverServer(serverRows, draft) {
    if (!draft?.rows?.length) {
        return serverRows;
    }
    if (!serverRows?.length) {
        return restoreRowsFromDraft(draft);
    }
    const merged = mergeIncomingRows(draft.rows, serverRows);
    const withDeleted = applyDeletedMetadataKeys(merged, draft.deletedMetadataKeys);
    return hydrateRowsMetadataFields(withDeleted, draft.metadataColumnKeys);
}

/**
 * @param {SdeRow[]} rows
 * @param {string[]|undefined} deletedMetadataKeys
 */
function applyDeletedMetadataKeys(rows, deletedMetadataKeys) {
    if (!deletedMetadataKeys?.length) {
        return rows;
    }
    return rows.map((row) => {
        const fields = { ...row.fields };
        deletedMetadataKeys.forEach((key) => {
            if (fields[key]) {
                fields[key] = { ...fields[key], deleted: true };
            }
        });
        return { ...row, fields };
    });
}

/**
 * @param {import('@/types/structured-data-editor').ApiExtractionRow[]} apiRows
 * @param {number} startIndex
 * @returns {SdeRow[]}
 */
export function apiRowsToIncomingSdeRows(apiRows, startIndex = 0) {
    return apiRowsToSdeRows(apiRows, startIndex);
}
